import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ChatItem, AssetPrompt, Message } from '../../molecules';
import { NewButton, Icon, Text } from '../../../../../components';
import { questionShape, userShape, sourceShape } from '../../../../../propsShape';

const AssetChartChatContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const ChartFollowUpsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledAssetPrompt = styled(AssetPrompt)`
  width: 100%;
  margin: 0 auto;
`;

const AssetPromptContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-top: auto;
`;

const BackToMainChatButton = styled(NewButton)`
  flex-grow: 0;
  align-self: flex-start;
  width: fit-content;
  margin-bottom: 32px;
`;

const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
  width: 100%;
`;

const SuggestionItem = styled(NewButton)`
  border-bottom: 1px solid var(--border-muted);
  border-radius: 0;
  justify-content: flex-start;

  &:hover {
    border-radius: 6px;
    border-bottom: 1px solid transparent;
  }
`;

const AssetChartChat = ({
  currentQuestion,
  handleCreateQuestion,
  handleRetryQuestion,
  handleDeleteQuestion,
  users,
  sources,
  handleSelectQuestion,
  switchToTableMode,
  currentUser,
  questions,
  isReadOnly = false,
  columnMentionItems = [],
  metrics = [],
  termTranslations = {}
}) => {
  const [prompt, setPrompt] = useState('');
  const editorRef = useRef(null);
  const insertMentionRef = useRef(null);

  // Ref to store the latest currentQuestion to avoid closure issues
  const currentQuestionRef = useRef(currentQuestion);

  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  const handleRetryClick = useCallback((question, evt) => {
    evt.stopPropagation();
    handleRetryQuestion(question);
  }, []);

  const handleUndoClick = useCallback(
    (questionId, evt) => {
      evt.stopPropagation();
      handleDeleteQuestion(questionId);
    },
    [handleDeleteQuestion]
  );

  const handleCreateChart = useCallback(
    (_prompt) => {
      const latestCurrentQuestion = currentQuestionRef.current;

      const questionOnWhichFollowWillBeCreated =
        latestCurrentQuestion.lastChartFollowUp ||
        latestCurrentQuestion.currentChartFollowUp ||
        latestCurrentQuestion;
      const command = questionOnWhichFollowWillBeCreated?.isChart ? 'chart-refine' : 'chart';

      setPrompt('');
      return handleCreateQuestion(
        _prompt || prompt,
        questionOnWhichFollowWillBeCreated?.id,
        command
      );
    },
    [prompt, handleCreateQuestion]
  );

  const handleSelectChartQuestion = useCallback(
    (question) => {
      const updatedQuestion = currentQuestion.setCurrentChartFollowUp(question);
      handleSelectQuestion(updatedQuestion);
    },
    [currentQuestion, handleSelectQuestion]
  );

  const renderChartQuestion = useCallback(
    (question) => {
      if (!question) return null;

      // Find chartFollowUps for this question
      const chartFollowUps = question.followUps.filter((f) => f.isChart) || [];
      // Choose active followUp
      const activeFollowUp = chartFollowUps.get(0) || null;

      return (
        <>
          <ChatItem
            key={question.id}
            question={question}
            isSaved={question.spaceIds.length > 0}
            isReadOnly={isReadOnly}
            users={users}
            currentUser={currentUser}
            sources={sources}
            onQuestionSelect={() => handleSelectChartQuestion(question)}
            onRetryQuestion={(evt) => handleRetryClick(question, evt)}
            // id here to handle the clarify question deletion
            onUndoClick={(evt, id) => handleUndoClick(id || question.id, evt)}
            isSelected={
              question.id ===
              (currentQuestion.currentChartFollowUp?.id ?? currentQuestion.lastChartFollowUp?.id)
            }
            isLastQuestion={!activeFollowUp}
          />
          {activeFollowUp && renderChartQuestion(activeFollowUp)}
        </>
      );
    },
    [
      currentQuestion,
      users,
      currentUser,
      sources,
      handleSelectChartQuestion,
      handleRetryClick,
      handleUndoClick,
      isReadOnly
    ]
  );

  // Find a question by ID in a deeply nested question structure
  const findQuestionById = useCallback((questionsList, targetId) => {
    if (!questionsList) return null;

    // Try to find the question at the current level
    const directMatch = questionsList.find((q) => q.id === targetId);
    if (directMatch) return directMatch;

    // If not found, recursively search through each question's followUps
    let result = null;
    questionsList.forEach((question) => {
      if (result) return; // Already found
      if (question.followUps && question.followUps.size > 0) {
        const nestedMatch = findQuestionById(question.followUps, targetId);
        if (nestedMatch) result = nestedMatch;
      }
    });

    return result;
  }, []);

  // Find the current question in the full questions tree
  // eslint-disable-next-line no-unused-vars
  const matchingQuestion = questions ? findQuestionById(questions, currentQuestion.id) : null;

  return (
    <AssetChartChatContainer>
      <BackToMainChatButton onClick={switchToTableMode}>
        <Icon name='arrow-left' />
        Return to main chat
      </BackToMainChatButton>
      {matchingQuestion?.followUps.filter((f) => f.isChart).size > 0 ? (
        <ChartFollowUpsContainer>
          {renderChartQuestion(matchingQuestion.followUps.filter((f) => f.isChart).get(0))}
        </ChartFollowUpsContainer>
      ) : (
        <Message
          text='To create a chart, specify the type (e.g., bar chart) and name the x-axis, y-axis, and title.'
          asset={
            <SuggestionsContainer>
              <Text>Here are some chart suggestions:</Text>
              <SuggestionItem secondary onClick={() => handleCreateChart('Create a bar chart')}>
                <Icon displaySize={20} name='bar-chart' color='var(--content-subtitle)' />
                <Text>Create a bar chart</Text>
              </SuggestionItem>
              <SuggestionItem secondary onClick={() => handleCreateChart('Create a line chart')}>
                <Icon displaySize={20} name='line-chart' color='var(--content-subtitle)' />
                <Text>Create a line chart</Text>
              </SuggestionItem>
              <SuggestionItem
                secondary
                onClick={() => handleCreateChart('Create a stacked bar chart')}
              >
                <Icon displaySize={20} name='stacked-bar-chart' color='var(--content-subtitle)' />
                <Text>Create a stacked bar chart</Text>
              </SuggestionItem>
            </SuggestionsContainer>
          }
        />
      )}
      <AssetPromptContainer>
        <StyledAssetPrompt
          ref={editorRef}
          insertMentionRef={insertMentionRef}
          disabled={isReadOnly}
          handleCreateQuestion={handleCreateChart}
          placeholder='Ask about this chart…'
          mentionItems={{
            columns: columnMentionItems,
            metrics
          }}
          termTranslations={termTranslations}
        />
      </AssetPromptContainer>
    </AssetChartChatContainer>
  );
};

AssetChartChat.propTypes = {
  currentQuestion: questionShape.isRequired,
  handleCreateQuestion: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  handleDeleteQuestion: PropTypes.func.isRequired,
  handleRetryQuestion: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(userShape).isRequired,
  sources: PropTypes.arrayOf(sourceShape).isRequired,
  handleSelectQuestion: PropTypes.func.isRequired,
  columnMentionItems: PropTypes.arrayOf(PropTypes.shape({})),
  metrics: PropTypes.arrayOf(PropTypes.shape({})),
  termTranslations: PropTypes.objectOf(PropTypes.string),
  switchToTableMode: PropTypes.func.isRequired,
  currentUser: userShape.isRequired,
  questions: PropTypes.shape({
    find: PropTypes.func,
    filter: PropTypes.func,
    get: PropTypes.func
  })
};

export { AssetChartChat };
